import { useQuery } from "@tanstack/react-query";
import { Card, Pill, fmtDataISO } from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { BackLink, PageHeader } from "./_ui";

// Aba Convenios da prefeitura — SOMENTE LEITURA (cliente 21/07/2026): a
// prefeitura acompanha TODA a config que a averbadora definiu; nao edita nada.
// Layout em cards com quadrados de pontas redondas.
interface ConvRow {
  id: string; nome: string; bancoNome: string; codigoVerba: string;
  dataCorte: number; diaRepasse: number;
  prazoTravaHoras: number; prazoPortabilidadeDU: number; prefixo: string; formatoImportacao: string;
  // Nulos de verdade: a API deixou de inventar 96 parcelas / 1,8% / 35% / 18-80
  // quando o convenio nao tem config (cliente 14/08/2026 — valor de convenio nao
  // tem default, tem que ser definido). Tipar como `number` fazia o TypeScript
  // garantir um valor que nunca existiu.
  maxParcelas: number | null; taxaMaxAm: number | null; maxComprometimentoPct: number | null;
  idadeMin: number | null; idadeMax: number | null;
  vigenciaInicio: string; vigenciaFim: string | null; vinculosAceitos: string[]; regrasEspeciais: string; ativo: boolean;
}

const eyebrow: React.CSSProperties = { fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" };
// fmtDate local removido — usa fmtDataISO do @atlas/ui/web (mesma impl).

/** Quadrado de estatistica — pontas redondas, rotulo + valor. */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: "var(--bg-elev-2)", border: "1px solid var(--border)", borderRadius: 14, padding: "12px 14px" }}>
      <div style={{ fontSize: 11, letterSpacing: ".06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, marginTop: 3 }}>{value}</div>
    </div>
  );
}

/**
 * Parametro do convenio: mostra o valor, ou diz que ainda nao foi definido.
 *
 * "Não definido" e' diferente de "—": um travessao le como ausencia de dado, e a
 * prefeitura precisa distinguir "a averbadora nao configurou isto ainda" de "isto
 * nao se aplica". Com valor inventado essa distincao era impossivel — 96 parcelas
 * apareciam iguais em convenio configurado e em convenio sem config nenhuma.
 */
function Param({ label, valor, sufixo = "", nota }: { label: string; valor: number | null; sufixo?: string; nota?: string }) {
  const definido = valor != null;
  return (
    <div style={{
      background: "var(--bg-elev-2)",
      border: `1px solid ${definido ? "var(--border)" : "var(--amber-500)"}`,
      borderRadius: 14, padding: "12px 14px",
    }}>
      <div style={{ fontSize: 11, letterSpacing: ".06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</div>
      <div style={{
        fontSize: definido ? 18 : 13,
        fontWeight: definido ? 800 : 600,
        marginTop: 3,
        color: definido ? "var(--text)" : "var(--amber-500)",
      }}>
        {definido ? `${valor}${sufixo}` : "Não definido"}
      </div>
      {nota ? <div style={{ fontSize: 10.5, color: "var(--text-dim)", marginTop: 4, lineHeight: 1.4 }}>{nota}</div> : null}
    </div>
  );
}

export function PrefeituraConvenios() {
  const q = useQuery({ queryKey: ["prefeitura", "convenios"], queryFn: () => atlas.prefeitura.convenios() });
  const convenios = (q.data?.convenios ?? []) as ConvRow[];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <BackLink fallback="/prefeitura/dashboard" />
      <PageHeader title="Regras de Convênio" subtitle="Configurados pela averbadora. Esta tela é somente leitura — a prefeitura acompanha todos os parâmetros definidos." />

      {q.isLoading ? (
        <Card><span style={{ color: "var(--text-muted)" }}>Carregando…</span></Card>
      ) : convenios.length === 0 ? (
        <Card><span style={{ color: "var(--text-muted)" }}>Nenhum convênio configurado pela averbadora ainda.</span></Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {convenios.map((c) => (
            <Card key={c.id}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800 }}>{c.nome}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{c.bancoNome}</div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Pill variant={c.ativo ? "emdia" : "expirado"}>{c.ativo ? "Vigente" : "Inativo"}</Pill>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-dim)", padding: "2px 8px", background: "var(--bg-elev-2)", borderRadius: 6 }}>{c.id}</span>
                </div>
              </div>

              {/* Quadrados de pontas redondas com toda a config (defensivo:
                  campos podem faltar se o isolate da API ainda nao propagou). */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
                {/* Dados do cadastro do convenio (modal "Editar" da averbadora) —
                    cliente pediu 23/07/2026 pra prefeitura enxergar tambem. */}
                <Stat label="Código verba" value={c.codigoVerba || "—"} />
                <Stat label="Dia de corte" value={c.dataCorte != null ? `Dia ${c.dataCorte}` : "—"} />
                <Stat label="Dia de repasse" value={c.diaRepasse != null ? `Dia ${c.diaRepasse}` : "—"} />
                <Stat label="Prefixo" value={c.prefixo || "—"} />
                <Stat label="Importação" value={c.formatoImportacao || "—"} />
                <Stat label="Trava regular" value={c.prazoTravaHoras != null ? `${c.prazoTravaHoras}h` : "—"} />
                <Stat label="Trava portabilidade" value={c.prazoPortabilidadeDU != null ? `${c.prazoPortabilidadeDU} DU` : "—"} />
                {/* Estes quatro sao os que a averbadora define por convenio. Sem
                    definicao, nao ha regra: o prazo cai no teto da plataforma
                    (120) e a taxa nao tem teto nenhum. */}
                <Param label="Máx. parcelas" valor={c.maxParcelas} nota={c.maxParcelas == null ? "Sem teto próprio: vale o limite da plataforma, 120." : undefined} />
                <Param label="Teto de CET a.m." valor={c.taxaMaxAm} sufixo="%" nota={c.taxaMaxAm == null ? "Sem teto de CET neste convênio." : "Empréstimo, portabilidade e refinanciamento com CET acima disso são recusados. Cartão não é limitado por este teto."} />
                <Param
                  label="Máx. comprometimento"
                  valor={c.maxComprometimentoPct != null ? Math.round(c.maxComprometimentoPct * 100) : null}
                  sufixo="%"
                  nota="Informativo — a margem usa os percentuais fixos da plataforma (35% / 5% / 5%)."
                />
                <div style={{
                  background: "var(--bg-elev-2)",
                  border: `1px solid ${(c.idadeMin != null && c.idadeMax != null) ? "var(--border)" : "var(--amber-500)"}`,
                  borderRadius: 14, padding: "12px 14px",
                }}>
                  <div style={{ fontSize: 11, letterSpacing: ".06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>Faixa etária</div>
                  <div style={{
                    fontSize: (c.idadeMin != null && c.idadeMax != null) ? 18 : 13,
                    fontWeight: (c.idadeMin != null && c.idadeMax != null) ? 800 : 600,
                    marginTop: 3,
                    color: (c.idadeMin != null && c.idadeMax != null) ? "var(--text)" : "var(--amber-500)",
                  }}>
                    {(c.idadeMin != null && c.idadeMax != null) ? `${c.idadeMin}–${c.idadeMax}` : "Não definida"}
                  </div>
                  <div style={{ fontSize: 10.5, color: "var(--text-dim)", marginTop: 4, lineHeight: 1.4 }}>
                    Informativa — nenhuma validação barra por idade.
                  </div>
                </div>
                <Stat label="Vigência" value={`${fmtDataISO(c.vigenciaInicio)} → ${fmtDataISO(c.vigenciaFim)}`} />
              </div>

              {/* Vínculos aceitos. */}
              <div style={{ marginTop: 14 }}>
                <div style={{ ...eyebrow, fontSize: 11, marginBottom: 6 }}>Vínculos aceitos</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {(c.vinculosAceitos ?? []).length > 0
                    ? (c.vinculosAceitos ?? []).map((v) => <Pill key={v} variant="aceita">{v}</Pill>)
                    : <span style={{ fontSize: 13, color: "var(--amber-500)", fontWeight: 600 }}>Não definidos</span>}
                </div>
                <div style={{ fontSize: 10.5, color: "var(--text-dim)", marginTop: 5, lineHeight: 1.4 }}>
                  Informativo — nenhuma validação recusa averbação por vínculo.
                </div>
              </div>

              {/* Regras especiais. */}
              {c.regrasEspeciais ? (
                <div style={{ marginTop: 14 }}>
                  <div style={{ ...eyebrow, fontSize: 11, marginBottom: 6 }}>Regras especiais</div>
                  <div style={{ background: "var(--bg-elev-2)", border: "1px solid var(--border)", borderRadius: 14, padding: "12px 14px", fontSize: 13, color: "var(--text-muted)" }}>
                    {c.regrasEspeciais}
                  </div>
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
